import { useQuery } from '@tanstack/react-query'
import { CalendarDays } from 'lucide-react'

import { businessDayQueryOptions } from '@/api/reference-api'
import { session } from '@/api/session'
import { useAuth } from '@/auth/use-auth'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { BusinessDayStatus } from '@/api/types'
import { formatBusinessDate } from '@/lib/datetime'
import { cn } from '@/lib/utils'

/**
 * The branch's open business date.
 *
 * This is the date postings land on, and it is not today's date on this machine: a branch that
 * has not yet closed yesterday is still working in yesterday, and a teller needs to see that
 * before they post. It is served by the ledger and displayed exactly as served.
 *
 * The stacked layout is for the compact menu the narrow top bar puts it in, where there is height
 * to spare and no width at all. It says the same thing in the same words.
 */
const STATUS_LABEL: Record<BusinessDayStatus, string> = {
  Open: 'Day open',
  // Distinct from both open and unopened on purpose: the date is settled and still the one to
  // display, but end-of-day checks are running and nothing new posts into it.
  Closing: 'Closing',
  NotOpened: 'Not opened',
}

const STATUS_TONE: Record<BusinessDayStatus, 'success' | 'warning'> = {
  Open: 'success',
  Closing: 'warning',
  NotOpened: 'warning',
}

export function BusinessDate({ layout = 'inline' }: { layout?: 'inline' | 'stacked' }) {
  const { user } = useAuth()
  const branchId = session.getBranchId() ?? user?.homeBranchId ?? null
  const { data, isPending, isError } = useQuery(businessDayQueryOptions(branchId))

  const stacked = layout === 'stacked'

  if (isPending) {
    return <Skeleton className={cn('h-8', stacked ? 'w-full' : 'w-44')} />
  }

  if (isError || !data) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted">
        <CalendarDays className="size-4 shrink-0" aria-hidden />
        Business date unavailable
      </span>
    )
  }

  return (
    <span
      className={cn(
        'text-sm',
        stacked ? 'flex flex-col items-start gap-1' : 'flex items-center gap-2',
      )}
    >
      <span className="flex items-center gap-2">
        <CalendarDays className="size-4 shrink-0 text-muted" aria-hidden />
        <span className="numeric font-medium text-heading tabular-nums">
          {/*
            No date rather than today's date. A branch with no open day has no ledger date at all,
            and filling the gap with the browser's date would show a teller a day they cannot post
            into as though they could.
          */}
          {data.businessDate ? formatBusinessDate(data.businessDate) : 'No business day open'}
        </span>
      </span>
      <Badge tone={STATUS_TONE[data.status] ?? 'warning'}>
        {STATUS_LABEL[data.status] ?? data.status}
      </Badge>
    </span>
  )
}
